import { useEffect } from 'react'

import { findNormalizedTextRanges } from '@/components/common/MarkdownViewer/findTextRanges'

import useStableCallback from '@/hooks/common/useEventEffect'

// Painted via the CSS Custom Highlight API so the captured selection stays
// visible after the DOM selection moves to the composer. Browsers without
// the API (or happy-dom) fall back to the old lose-the-highlight behavior.
export const KEPT_SELECTION_HIGHLIGHT = 'sweetspot-kept-text-selection'

// The app's dark mode is class-driven (`darkMode: 'class'` + next-themes), so
// scope the dark tint to the `.dark` root class instead of a
// `prefers-color-scheme` query that can disagree with the app chrome.
export const keptHighlightStyle = (name: string) =>
  `::highlight(${name}){background-color:rgb(180 213 254 / 0.9)}
.dark ::highlight(${name}){background-color:rgb(37 99 235 / 0.5)}`

export const KEPT_SELECTION_STYLE = keptHighlightStyle(KEPT_SELECTION_HIGHLIGHT)

export const highlightRegistry = (): HighlightRegistry | undefined =>
  typeof CSS !== 'undefined' &&
  'highlights' in CSS &&
  typeof Highlight === 'function'
    ? CSS.highlights
    : undefined

export interface TextSelectionHighlightOptions {
  /**
   * Text currently selected inside the container, or null when the selection
   * collapses inside it (an explicit deselect).
   */
  onTextSelectionChange?: (text: string | null) => void
  /**
   * Previously captured text to re-highlight when the viewer re-opens
   * (selection chips, citation navigation).
   */
  highlightText?: string | null
  /** Changes when the caller explicitly requests the same highlight again. */
  highlightKey?: number
  /** Defers the highlight search until the viewer has content to search. */
  contentLoaded?: boolean
}

/**
 * One owner for every highlight a flowing-text viewer paints, mirroring the
 * sandboxed HTML shell's store (buildHtmlViewerShell.ts). A single kept-range
 * store backs both a live capture and a restored highlight, so every
 * transition below knows how to clear both:
 *
 *   restore(text, key)             -> KEPT   paint ranges + scroll into view
 *   select inside the viewer       -> LIVE   store ranges, native paint only
 *   selection leaves the viewer    -> KEPT   repaint store (composer focus)
 *   collapse inside the viewer     -> EMPTY  clear store + paint, report null
 *   anchorless clear (chip dismiss)-> EMPTY  clear store + paint
 *
 * Splitting restore into its own effect is what previously made a restored
 * highlight undismissable: the deselect transitions only unpainted their own
 * Highlight instance, never the restore's.
 *
 * Shared by every flowing-text viewer that renders in the top document
 * (markdown, plain text/code); the sandboxed HTML preview re-implements the
 * same machine across its frame boundary.
 */
export const useTextSelectionHighlights = (
  containerRef: React.RefObject<HTMLElement | null>,
  {
    onTextSelectionChange,
    highlightText,
    highlightKey,
    contentLoaded = false,
  }: TextSelectionHighlightOptions,
): void => {
  const stableOnTextSelectionChange = useStableCallback(
    (selected: string | null) => {
      onTextSelectionChange?.(selected)
    },
  )
  const textSelectionChangeEnabled = onTextSelectionChange !== undefined
  const restoreEnabled = Boolean(highlightText) && contentLoaded
  useEffect(() => {
    // The listener also drives deselect for a restored highlight, so it must
    // run whenever either half is active.
    if (!textSelectionChangeEnabled && !restoreEnabled) return

    let timeoutId: number | null = null
    // The kept ranges: a captured selection or a restored highlight, cloned
    // so they can be repainted after the live DOM selection moves away.
    let keptRanges: Range[] = []
    let painted: Highlight | null = null
    const unpaint = () => {
      const registry = highlightRegistry()
      if (
        registry &&
        painted &&
        registry.get(KEPT_SELECTION_HIGHLIGHT) === painted
      ) {
        registry.delete(KEPT_SELECTION_HIGHLIGHT)
      }
      painted = null
    }
    const paintKept = () => {
      const registry = highlightRegistry()
      if (!registry || keptRanges.length === 0) return
      const highlight = new Highlight()
      for (const range of keptRanges) highlight.add(range)
      painted = highlight
      registry.set(KEPT_SELECTION_HIGHLIGHT, highlight)
    }

    // Content can mount asynchronously (markdown segments, dynamic blocks
    // such as diagrams commit after the surrounding prose). Search after the
    // current paint and retry briefly so card navigation does not race those
    // commits. Any user selection event inside the viewer cancels the retries:
    // the user acted, so a late repaint must not resurrect the highlight.
    const restoreTimeoutIds: number[] = []
    const cancelRestoreAttempts = () => {
      for (const restoreTimeoutId of restoreTimeoutIds) {
        window.clearTimeout(restoreTimeoutId)
      }
      restoreTimeoutIds.length = 0
    }
    const tryRestore = () => {
      const container = containerRef.current
      if (!container || !highlightText) return

      const ranges = findNormalizedTextRanges(container, highlightText)
      if (ranges.length === 0) return

      keptRanges = ranges
      unpaint()
      paintKept()

      const startContainer = ranges[0].startContainer
      const anchor =
        startContainer instanceof Element
          ? startContainer
          : startContainer.parentElement
      if (anchor && typeof anchor.scrollIntoView === 'function') {
        anchor.scrollIntoView({ block: 'center' })
      }
      cancelRestoreAttempts()
    }

    const emitSelection = () => {
      timeoutId = null
      const selection = window.getSelection()
      const container = containerRef.current
      if (!selection || !container) return
      if (!selection.anchorNode || !selection.focusNode) {
        // An anchorless selection is an explicit clear (the composer chip's
        // dismiss calls removeAllRanges): stop painting the kept highlight.
        // The capture itself is stripped store-side, so no emit here.
        cancelRestoreAttempts()
        keptRanges = []
        unpaint()
        return
      }
      // Composer focus, Send clicks, and app-level selections move the DOM
      // selection outside the viewer. Keep the captured or restored ranges -
      // and keep painting them so the user still sees what travels with the
      // message.
      if (
        !container.contains(selection.anchorNode) ||
        !container.contains(selection.focusNode)
      ) {
        paintKept()
        return
      }
      // A collapse inside the rendered document is an explicit deselect.
      if (selection.isCollapsed) {
        cancelRestoreAttempts()
        keptRanges = []
        unpaint()
        stableOnTextSelectionChange(null)
        return
      }
      cancelRestoreAttempts()
      keptRanges = Array.from({ length: selection.rangeCount }, (_, index) =>
        selection.getRangeAt(index).cloneRange(),
      )
      // The live selection paints natively; the kept highlight would
      // double-tint it.
      unpaint()
      stableOnTextSelectionChange(selection.toString())
    }
    // Leading + trailing throttle. A trailing-only debounce resets on
    // every mousemove of a drag, so nothing is emitted (and the composer
    // chip stays hidden) until the drag pauses. Emitting the first event
    // right away and then at most every 100ms keeps the chip live during
    // the drag, with a trailing emit for the final range.
    let lastEmitAt = 0
    const handleSelectionChange = () => {
      if (timeoutId !== null) return
      const delay = Math.max(0, 100 - (Date.now() - lastEmitAt))
      timeoutId = window.setTimeout(() => {
        lastEmitAt = Date.now()
        emitSelection()
      }, delay)
    }
    document.addEventListener('selectionchange', handleSelectionChange)
    if (restoreEnabled) {
      for (const delay of [0, 300, 900]) {
        restoreTimeoutIds.push(window.setTimeout(tryRestore, delay))
      }
    }
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      cancelRestoreAttempts()
      unpaint()
    }
    // highlightKey re-applies the same highlight text on repeat navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    containerRef,
    stableOnTextSelectionChange,
    textSelectionChangeEnabled,
    restoreEnabled,
    highlightText,
    highlightKey,
  ])
}
